import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseModalTitle from '@monorepo/shared/src/components/common/Modals/BaseModalTitle';
import { useTour } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Tour';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import { updateUserSetting } from '@monorepo/shared/src/api';
import { getSetting } from 'graph/selectors/setting-selector';
import {
  isActiveLicense,
  isTrialLicense,
} from '@monorepo/shared/src/graph/selectors/license-selector';
import useUserStore from '@monorepo/shared/src/graph/hooks/use-user';
import { Settings } from 'types';
import ExpiredModal from '../common/Modals/ExpiredModal';
import WelcomeModal from '../modules/Home/components/WelcomeModal';

const t = (key: string, options?) => translate('modal', key, options);

const AppLoadListeners: React.FC = (props) => {
  const navigate = useNavigate();

  const [user, { userRefetch }] = useUserStore();
  const [{ isTourInProgress }] = useTour();

  const [, { openModal }] = useModal();

  const license = user.me?.licenses?.[0];

  const updateLicenseSetting = useCallback(async () => {
    try {
      updateUserSetting(Settings.FREE_TRIAL, {
        freeTrial: { viewedWelcomeModal: true },
      });
    } catch (error) {
      console.log(error);
    }
  }, []);

  const handleModalExit = async () => {
    await updateLicenseSetting();
    await userRefetch();
  };

  const shouldDisplayFreeTrialWelcomeModal = () => {
    const isTrial = isTrialLicense(license);
    const freeTrialSetting = getSetting(user?.me?.settings, Settings.FREE_TRIAL);
    const hasViewedFreeTrialWelcomeModal = freeTrialSetting?.value?.viewedWelcomeModal;
    return isTrial && isActiveLicense(license) && !hasViewedFreeTrialWelcomeModal;
  };

  const getShouldDisplayOnboardingModal = () => {
    const toursSettings = user?.me?.toursSettings;
    if (!toursSettings?.length) return true;
    const onboardingTour = user?.me?.toursSettings?.find(
      (setting) => setting.tour?.name === Settings.WELCOME
    );
    return !!onboardingTour?.tourStepSettings?.filter((tourStepSetting) => !tourStepSetting.viewed)
      ?.length;
  };

  const shouldDisplayOnboardingModal = getShouldDisplayOnboardingModal();

  const displayTourModal = () => {
    if (!shouldDisplayOnboardingModal || isTourInProgress) return;
    navigate('/tour/onboarding');
  };

  const displayExpiredLicenseModal = () => {
    openModal({
      Title: () => <BaseModalTitle text={t('licenseExpired')} hideCloseButton />,
      body: <ExpiredModal />,
    });
  };

  const displayFreeTrialWelcomeModal = () => {
    openModal({
      Title: () => <BaseModalTitle text={t('freeTrialTitle')} onClose={handleModalExit} />,
      body: <WelcomeModal handleModalExit={handleModalExit} />,
    });
  };

  useEffect(() => {
    if (!user.isAuthenticated) return;

    const chooseModal = () => {
      if (license && !isActiveLicense(license)) {
        displayExpiredLicenseModal();
        return;
      }

      if (shouldDisplayFreeTrialWelcomeModal()) {
        displayFreeTrialWelcomeModal();
        return;
      }
      displayTourModal();
    };

    setTimeout(() => {
      chooseModal();
    }, 500);
  }, [license, user.isAuthenticated, shouldDisplayOnboardingModal]);

  return <>{props.children}</>;
};

export default AppLoadListeners;
